import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import { useIsSkincare } from 'hooks/useIsSkincare';
import {
  FULLSCREEN_POPUP,
  SKINCARE_FULLSCREEN_POPUP,
} from 'lib/queries/common';
import {
  filterPostsByCategory,
  PRODUCTS_QUERY,
  reviewedProductsQuery,
  siteSettingsQuery,
} from 'lib/queries/globals';
import { client } from 'lib/sanity';
import { parseGraphQLError } from 'lib/shopify';
import { ShopifyService } from 'services/shopify';
import type { Globals, GlobalsRaw } from 'types/cms/Globals';
import type { ProductType } from 'types/product/Product';
import { sortCountriesByPreferredOrder } from 'utils/sortCountries';

export type GlobalsQueryKey = [string];

type GlobalsQuery = UseQueryResult & {
  data: Globals;
};

export const GLOBALS_KEY = 'globals';

export const fetchGlobals = async () => {
  try {
    const query = groq`
    {
      'siteSettings': ${siteSettingsQuery('supplements')},
      'skincareSiteSettings': ${siteSettingsQuery('skincare')},
      'products': ${PRODUCTS_QUERY},
      'fullscreenPopup': ${FULLSCREEN_POPUP},
      'skincareFullscreenPopup': ${SKINCARE_FULLSCREEN_POPUP},
      'posts': ${filterPostsByCategory('nutrition')},
      'skincarePosts': ${filterPostsByCategory('skincare')},
      'reviewsMainProductSlugs': ${reviewedProductsQuery('supplements')},
      'skincareReviewsMainProductSlugs': ${reviewedProductsQuery('skincare')},
    }
  `;

    const globals = await client.fetch<GlobalsRaw>(query);

    const shopifyProductsResponse = await ShopifyService.getProducts();
    const availableCountriesResponse =
      await ShopifyService.getAvailableCountries();

    const shopifyProducts = shopifyProductsResponse.products.edges.map(
      (item) => ({
        id: item.node.id,
        handle: item.node.handle,
        title: item.node.title,
        productType: item.node.productType as ProductType,
        availableForSale: item.node.availableForSale,
        variants: item.node.variants.edges.map((variant) => ({
          id: variant.node.id,
          title: variant.node.title,
          availableForSale: variant.node.availableForSale,
        })),
      }),
    );

    return {
      ...globals,
      shopifyProducts,
      availableCountries: sortCountriesByPreferredOrder({
        countries: availableCountriesResponse.localization.availableCountries,
      }),
    };
  } catch (error) {
    /* INFO: We catch and re-throw purely for logging, the error
     * is captured by React Query, see below. */
    console.error('[useGlobals][fetchGlobals] Error fetching globals');
    throw Error(`[useGlobals][fetchGlobals] ${parseGraphQLError(error)}`);
  }
};

export const useGlobals = () => {
  const isSkincare = useIsSkincare();

  const query = useQuery({
    queryKey: [GLOBALS_KEY],
    queryFn: fetchGlobals,
    /* Re-throwing the error here will not be caught and break at build time. */
    throwOnError: true,
    select: (data) => {
      const {
        siteSettings,
        reviewsMainProductSlugs = [],
        posts,
        skincareSiteSettings,
        skincareReviewsMainProductSlugs = [],
        skincarePosts,
        fullscreenPopup,
        skincareFullscreenPopup,
        ...rest
      } = data;

      const reviewsMainProductSlugsAllCategories = [
        ...skincareReviewsMainProductSlugs,
        ...reviewsMainProductSlugs,
      ];

      if (isSkincare) {
        return {
          siteSettings: skincareSiteSettings,
          reviewsMainProductSlugs: skincareReviewsMainProductSlugs,
          reviewsMainProductSlugsAllCategories,
          posts: skincarePosts,
          fullscreenPopup: skincareFullscreenPopup,
          ...rest,
        } satisfies Globals;
      }

      return {
        siteSettings,
        reviewsMainProductSlugs,
        reviewsMainProductSlugsAllCategories,
        posts,
        fullscreenPopup,
        ...rest,
      } satisfies Globals;
    },
  });

  return query as GlobalsQuery;
};
